/* ==========================================================================
   Glasshaus · Product (PDP) behaviour

   - gallery: cross-fade slides, thumbnails, prev/next, keyboard arrows
   - variants: swap gallery/price/form input without a page reload
   - description: "Read more" un-clamps the copy
   - accordions: height-animated <details>
   - sticky bar: appears once the main add-to-bag scrolls past, with a
     read-progress line; its add button proxies the real form submit

   Progressive enhancement throughout: swatches are real links and the
   accordions are real <details>, so everything still works without JS.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initPdp(root) {
    if (root.dataset.ghPdpReady === 'true') return;
    root.dataset.ghPdpReady = 'true';

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gh-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gh-thumb]'));
    var index = 0;
    var stage = root.querySelector('[data-gh-stage]');
    var galleryCurrent = root.querySelector('[data-gh-current]');
    var mediaProgress = root.querySelector('[data-gh-media-progress]');
    var lightbox = root.querySelector('[data-gh-lightbox]');
    var lightboxImage = root.querySelector('[data-gh-lightbox-image]');
    var lightboxCurrent = root.querySelector('[data-gh-lightbox-current]');

    function formatCount(value) {
      return value < 10 ? '0' + value : String(value);
    }

    function syncLightbox() {
      if (!lightboxImage || !slides[index]) return;
      var activeSlide = slides[index];
      lightboxImage.src = activeSlide.getAttribute('data-gh-full-src') || '';
      lightboxImage.alt = activeSlide.getAttribute('data-gh-full-alt') || '';
      lightboxImage.width = parseInt(activeSlide.getAttribute('data-gh-full-width'), 10) || 1600;
      lightboxImage.height = parseInt(activeSlide.getAttribute('data-gh-full-height'), 10) || 1600;
      if (lightboxCurrent) lightboxCurrent.textContent = formatCount(index + 1);
    }

    /* Resize the stage to the incoming image's proportions. Products mix
       landscape and portrait shots, so a single fixed frame would crop one or
       the other; the frame follows the artwork instead. */
    function syncStageRatio() {
      if (!stage || !slides[index]) return;
      var ratio = slides[index].getAttribute('data-gh-ratio');
      if (ratio && ratio.indexOf('/') !== -1 && ratio.indexOf('0 /') !== 0) {
        stage.style.setProperty('--gh-pdp-media-ratio', ratio);
      }
    }

    /* ---------------------------------------------------------------- gallery */
    function show(next) {
      if (!slides.length) return;
      var total = slides.length;
      index = ((next % total) + total) % total;
      syncStageRatio();
      slides.forEach(function (slide, i) {
        var active = i === index;
        slide.classList.toggle('is-active', active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-pressed', String(i === index));
      });
      if (galleryCurrent) galleryCurrent.textContent = formatCount(index + 1);
      if (mediaProgress) mediaProgress.style.transform = 'scaleX(' + ((index + 1) / total) + ')';
      if (lightbox && lightbox.open) syncLightbox();
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        show(i);
      });
    });

    var prev = root.querySelector('[data-gh-prev]');
    var next = root.querySelector('[data-gh-next]');
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    var openZoom = root.querySelector('[data-gh-open-zoom]');
    var closeZoom = root.querySelector('[data-gh-close-lightbox]');
    var lightboxPrev = root.querySelector('[data-gh-lightbox-prev]');
    var lightboxNext = root.querySelector('[data-gh-lightbox-next]');

    function closeLightbox() {
      if (!lightbox) return;
      if (typeof lightbox.close === 'function') lightbox.close();
      else lightbox.removeAttribute('open');
      document.body.classList.remove('gh-pdp-lightbox-open');
    }

    if (openZoom && lightbox) {
      openZoom.addEventListener('click', function () {
        syncLightbox();
        document.body.classList.add('gh-pdp-lightbox-open');
        if (typeof lightbox.showModal === 'function') lightbox.showModal();
        else lightbox.setAttribute('open', '');
        if (closeZoom) closeZoom.focus({ preventScroll: true });
      });
      if (closeZoom) closeZoom.addEventListener('click', closeLightbox);
      if (lightboxPrev) lightboxPrev.addEventListener('click', function () { show(index - 1); });
      if (lightboxNext) lightboxNext.addEventListener('click', function () { show(index + 1); });
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox || e.target.classList.contains('gh-pdp__lightbox-inner')) closeLightbox();
      });
      lightbox.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { show(index - 1); e.preventDefault(); }
        if (e.key === 'ArrowRight') { show(index + 1); e.preventDefault(); }
      });
      lightbox.addEventListener('close', function () {
        document.body.classList.remove('gh-pdp-lightbox-open');
        if (openZoom) openZoom.focus({ preventScroll: true });
      });
    }

    var gallery = root.querySelector('[data-gh-gallery]');
    if (gallery) {
      gallery.setAttribute('tabindex', '0');
      gallery.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { show(index - 1); e.preventDefault(); }
        if (e.key === 'ArrowRight') { show(index + 1); e.preventDefault(); }
      });

      // Horizontal swipes change imagery; vertical gestures remain native page
      // scrolling. Passive listeners keep the main thread out of the way.
      var touchStartX = 0;
      var touchStartY = 0;
      gallery.addEventListener(
        'touchstart',
        function (e) {
          if (!e.touches.length) return;
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        },
        { passive: true }
      );
      gallery.addEventListener(
        'touchend',
        function (e) {
          if (!e.changedTouches.length) return;
          var deltaX = e.changedTouches[0].clientX - touchStartX;
          var deltaY = e.changedTouches[0].clientY - touchStartY;
          if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
          show(deltaX < 0 ? index + 1 : index - 1);
        },
        { passive: true }
      );
    }

    show(0);

    /* --------------------------------------------------------------- variants */
    var swatches = Array.prototype.slice.call(root.querySelectorAll('[data-gh-variant]'));
    var variantInput = root.querySelector('[data-gh-variant-input]');
    var priceEl = root.querySelector('[data-gh-price]');
    var barPrice = root.querySelector('[data-gh-bar-price]');
    var barThumb = root.querySelector('[data-gh-bar-thumb]');
    var atb = root.querySelector('[data-gh-atb]');
    var atbLabel = root.querySelector('[data-gh-atb-label]');
    var skuEl = root.querySelector('[data-gh-sku]');
    var barAdd = root.querySelector('[data-gh-bar-add]');
    var barAddLabel = root.querySelector('[data-gh-bar-add-label]');
    var optionValues = Array.prototype.slice.call(root.querySelectorAll('[data-gh-option-value]'));

    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function (e) {
        // let modified clicks (new tab, etc.) behave normally
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();

        var id = swatch.getAttribute('data-gh-variant');
        var price = swatch.getAttribute('data-gh-variant-price');
        var available = swatch.getAttribute('data-gh-variant-available') === 'true';
        var mediaIndex = parseInt(swatch.getAttribute('data-gh-variant-media'), 10);
        var sku = swatch.getAttribute('data-gh-variant-sku') || '';

        swatches.forEach(function (s) { s.classList.remove('is-active'); });
        swatches.forEach(function (s) { s.removeAttribute('aria-current'); });
        swatch.classList.add('is-active');
        swatch.setAttribute('aria-current', 'true');

        if (variantInput) {
          variantInput.value = id;
          variantInput.disabled = !available;
        }
        if (priceEl && price) priceEl.textContent = price;
        if (barPrice && price) barPrice.textContent = price;

        var img = swatch.querySelector('img');
        if (barThumb && img) barThumb.src = img.src;

        if (atb) {
          atb.disabled = !available;
          atb.setAttribute('aria-disabled', String(!available));
          if (atbLabel) atbLabel.textContent = available ? (atb.dataset.addLabel || 'Add to bag') : 'Sold out';
        }
        if (barAdd) {
          barAdd.disabled = !available;
          barAdd.setAttribute('aria-disabled', String(!available));
        }
        if (barAddLabel) barAddLabel.textContent = available ? 'Add' : 'Sold out';
        if (skuEl) skuEl.textContent = sku ? 'Style ' + sku : '';

        var values = (swatch.getAttribute('data-gh-variant-options') || '').split('||');
        optionValues.forEach(function (option, optionIndex) {
          if (values[optionIndex]) option.textContent = values[optionIndex];
        });

        if (!isNaN(mediaIndex)) show(mediaIndex);

        // keep the URL shareable without reloading
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', id);
          window.history.replaceState({}, '', url.toString());
        } catch (err) {
          /* URL unsupported — the href fallback still works on reload */
        }
      });
    });

    /* -------------------------------------------------------------- read more */
    var readmore = root.querySelector('[data-gh-readmore]');
    var clamped = root.querySelector('[data-gh-clamp]');
    if (readmore && clamped) {
      // hide the control entirely when the copy already fits
      if (clamped.scrollHeight <= clamped.clientHeight + 4) {
        readmore.style.display = 'none';
      }
      readmore.addEventListener('click', function () {
        var open = clamped.classList.toggle('is-open');
        readmore.setAttribute('aria-expanded', String(open));
        var label = readmore.querySelector('[data-gh-readmore-label]');
        if (label) label.textContent = open ? 'Read less' : 'Read more';
      });
    }

    /* ------------------------------------------------------------ accordions */
    Array.prototype.slice.call(root.querySelectorAll('[data-gh-acc]')).forEach(function (acc) {
      var panel = acc.querySelector('.gh-pdp__acc-panel');
      var summary = acc.querySelector('summary');
      if (!panel || !summary) return;

      function setHeight(open) {
        if (REDUCED) {
          panel.style.height = open ? 'auto' : '0px';
          return;
        }
        if (open) {
          panel.style.height = panel.scrollHeight + 'px';
          // release to auto once the transition lands, so reflow is safe
          panel.addEventListener('transitionend', function done(e) {
            if (e.propertyName !== 'height') return;
            panel.style.height = 'auto';
            panel.removeEventListener('transitionend', done);
          });
        } else {
          panel.style.height = panel.scrollHeight + 'px';
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { panel.style.height = '0px'; });
          });
        }
      }

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (acc.open) {
          setHeight(false);
          var onEnd = function (ev) {
            if (ev.propertyName !== 'height') return;
            acc.open = false;
            panel.removeEventListener('transitionend', onEnd);
          };
          if (REDUCED) { acc.open = false; } else { panel.addEventListener('transitionend', onEnd); }
        } else {
          acc.open = true;
          setHeight(true);
        }
      });
    });

    /* ------------------------------------------------------------ sticky bar */
    var bar = root.querySelector('[data-gh-stickybar]');
    var progress = root.querySelector('[data-gh-progress] span');
    var rail = root.querySelector('[data-gh-rail]');
    if (barAdd && atb) {
      barAdd.addEventListener('click', function () {
        atb.click();
      });
    }

    // offset the bar below whatever the header currently occupies
    var header = document.querySelector('#site-header') || document.querySelector('.header');
    function syncBarTop() {
      if (!header) return;
      var h = Math.round(header.getBoundingClientRect().height);
      if (h) root.style.setProperty('--gh-pdp-bar-top', h + 'px');
    }
    syncBarTop();
    window.addEventListener('resize', syncBarTop, { passive: true });

    if (bar && atb) {
      var ticking = false;
      function update() {
        ticking = false;
        var rect = atb.getBoundingClientRect();
        var past = rect.bottom < (parseInt(root.style.getPropertyValue('--gh-pdp-bar-top'), 10) || 66);
        bar.classList.toggle('is-visible', past);
        bar.setAttribute('aria-hidden', past ? 'false' : 'true');
        bar.inert = !past;

        if (progress) {
          var scrollable = document.documentElement.scrollHeight - window.innerHeight;
          var pct = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
          progress.style.width = Math.min(100, Math.max(0, pct)).toFixed(2) + '%';
        }
      }
      window.addEventListener(
        'scroll',
        function () {
          if (!ticking) {
            ticking = true;
            requestAnimationFrame(update);
          }
        },
        { passive: true }
      );
      update();
    }

    // surface add-to-cart failures next to the button
    var form = root.querySelector('form[data-type="add-to-cart-form"]');
    var errorEl = root.querySelector('[data-gh-form-error]');
    if (form && errorEl) {
      form.addEventListener('submit', function () {
        errorEl.textContent = '';
      });
    }

    if (rail) rail.setAttribute('data-gh-rail-ready', 'true');

    /* ------------------------------------------------------ staged reveal */
    var revealItems = Array.prototype.slice.call(root.querySelectorAll('[data-gh-reveal]'));
    if (!REDUCED && revealItems.length && 'IntersectionObserver' in window) {
      root.classList.add('is-motion-ready');
      var revealObserver = new IntersectionObserver(
        function (entries, observer) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px -8% 0px' }
      );
      revealItems.forEach(function (item) { revealObserver.observe(item); });
    } else {
      revealItems.forEach(function (item) { item.classList.add('is-visible'); });
    }
  }

  /* ---------------------------------------------------------- product rail */
  function initRail(section) {
    if (section.dataset.ghRailReady === 'true') return;
    section.dataset.ghRailReady = 'true';

    var viewport = section.querySelector('[data-gh-rail-viewport]');
    var bar = section.querySelector('[data-gh-rail-progress] span');
    if (!viewport || !bar) return;

    function update() {
      var max = viewport.scrollWidth - viewport.clientWidth;
      // thumb width mirrors how much of the rail is on screen
      var ratio = viewport.clientWidth / Math.max(viewport.scrollWidth, 1);
      var thumb = Math.max(12, Math.min(100, ratio * 100));
      bar.style.width = thumb + '%';
      var pct = max > 0 ? viewport.scrollLeft / max : 0;
      var travel = 100 - thumb;
      bar.style.transform = 'translateX(' + (pct * travel * (100 / Math.max(thumb, 1))) + '%)';
    }

    var ticking = false;
    viewport.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(function () {
            ticking = false;
            update();
          });
        }
      },
      { passive: true }
    );
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  function boot() {
    Array.prototype.slice.call(document.querySelectorAll('[data-gh-pdp]')).forEach(initPdp);
    Array.prototype.slice.call(document.querySelectorAll('[data-gh-rail-section]')).forEach(initRail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // re-init after theme-editor section reloads
  document.addEventListener('shopify:section:load', boot);
})();
